import { Backdrop, Box, Button, CircularProgress, Grid, Stack } from '@mui/material'
import { GridLabel, Heading1, PageLoaderContainer } from './ClientConfigToolStyles'
import React, { FormEvent, useEffect, useState } from 'react'
import clientConfigState, { packageFeatureState } from '../atoms/clientConfigState'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import ClientDetails from './ClientDetails'
import ClientSettings from './ClientSettings'
import ClientsService from '../service/clients'
import DisableConfirmDialog from './DisableConfirmDialog'
import Notification from '../util/Notification'

function ClientSettingsPage() {
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()
  const resetClientState = useResetRecoilState(clientConfigState)
  const updateClientState = useSetRecoilState(clientConfigState)
  const clientState = useRecoilValue(clientConfigState)
  const updatePackageFeatureState = useSetRecoilState(packageFeatureState)
  const packagesArray = useRecoilValue(packageFeatureState)
  const { config, data } = clientState

  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pageLoader, setPageLoader] = useState(true)
  const [backdrop, setBackdrop] = useState(false)

  const returnToSearch = () => {
    updateClientState({ ...clientState, data: { ...clientState.data, client_id: 0 } })
    navigate('/client-config-tool')
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    const requestPayload = {
      ClientName: data.client_name,
      trackerI9InternalData: {
        enableTrackerI9: data.enabled,
        package: data.package,
      },
    }
    const backdropTimeout = setTimeout(() => {
      setBackdrop(true)
    }, 3000)

    const request = config.isUpdate 
      ? ClientsService.updateClient(data.client_id, requestPayload) 
      : ClientsService.createClient(data.client_id, requestPayload)

    request
      .then(() => {
        updateClientState({
          ...clientState,
          config: {
            ...config,
            alertMessage: 'Your recent changes have been saved successfully',
            alertSeverity: 'success',
            alertTitle: 'Success',
            isUpdate: true,
            showAlert: true,
          },
        })
      })
      .catch(e => {
        updateClientState({
          ...clientState,
          config: {
            ...config, 
            alertMessage: e.response.data.responseMessage,
            alertSeverity: 'error',
            alertTitle: `Error - ${e.response.data.responseCode}`,
            showAlert: true,
          },
        })
      })
      .finally(() => {
        clearTimeout(backdropTimeout)
        setSubmitting(false)
        setBackdrop(false)
      })
  }

  useEffect(() => {
    let clientReq = null
    const client = location.state ? location.state.client : null
    const { clientId } = params
    if (!client) {
      clientReq = ClientsService
        .getTRClient(clientId)
        .then((response) => {
          const clientData = {
            client_city: response.city,
            client_id: response.id,
            client_name: response.name,
            client_phone: response.phone,
            client_state: response.state,
            client_street: response.street,
            client_street2: response.street2,
            client_zip: response.zip,
          }
          updateClientState({
            ...clientState,
            data: {
              ...data,
              ...clientData,
              value: `${response.id}`,
            },
          })
          return clientData
        })
        .catch(() => {
          navigate('/client-config-tool', { state: { error: 'The URL you’ve provided does not exist. Please try searching for the client by name.' } })
        })
    } else {
      clientReq = Promise.resolve(client)
    }

    const packageReq = ClientsService.getPackageAndFeatures()
      .then(response => {
        updatePackageFeatureState(response)
        return response
      })

    Promise.
      all([packageReq, clientReq])
      .then(responses => {
        const clientData = responses[1]
        if (clientData) {
          ClientsService
            .getClient(clientData.client_id)
            .then((response) => {
              updateClientState({
                ...clientState, 
                config: {
                  ...config,
                  isUpdate: true,
                }, 
                data: {
                  ...data,
                  ...clientData,
                  enabled: response.enabled,
                  everify: response.features.includes('EmployerEVerify'),
                  package: response.packageId,
                  trackerClientId: response.trackerClientId,
                  viewMigratedData: response.viewMigratedData,
                },
              })
            })
            .catch(() => {
            // it will be initial setup
              updateClientState({
                ...clientState, 
                config: {
                  ...config,
                  isUpdate: false,
                }, 
                data: {
                  ...data,
                  ...clientData,
                  enabled: false,
                  everify: false,
                  package: 0,
                },
              })
            })
            .finally(() => {
              setPageLoader(false)
            })
        }
      })
    
    return () => {
      resetClientState()
    }
  }, [])

  if (pageLoader) {
    return (
      <PageLoaderContainer>
        <CircularProgress sx={{ marginBottom: '1.5rem' }} />
        <GridLabel>{'Loading the client’s settings.'}</GridLabel>
        <GridLabel>{'This may take several seconds.'}</GridLabel>
      </PageLoaderContainer>
    )
  }
  return (
    <Box padding={3.75}>
      {backdrop
        && (
          <Backdrop
            open
            sx={{ color: '#00000080', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <PageLoaderContainer>
              <CircularProgress sx={{ marginBottom: '1.5rem', marginTop: '1.5rem' }} />
              <GridLabel sx={{ marginBottom: '1.5rem' }} >{'Saving changes'}</GridLabel>
            </PageLoaderContainer>
          </Backdrop>
        )
      }
      {config.showAlert
        && (
          <Notification
            autoHide={true}
            description={config.alertMessage}
            onClose={() => {
              updateClientState({
                ...clientState, 
                config: {
                  ...clientState.config,
                  showAlert: false,
                }
              })
            }}
            severity={config.alertSeverity}
            title={config.alertTitle}
          />
        )
      }
      <Stack spacing={2.2}>
        <Heading1>Configure Client Settings</Heading1>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <ClientDetails client={data} />
            <ClientSettings client={data} packagesArray={packagesArray} setBackdrop={setBackdrop} />
            <Grid alignItems="flex-end" columnSpacing={3.8} container justifyContent="flex-end" paddingRight={submitting ? 3 : 1} rowSpacing={1}>
              <Grid item>
                <Button color="primary" onClick={returnToSearch}>
                  Return to search
                </Button>
              </Grid>
              <Grid item xs={1}>
                {data.enabled
                  ? (
                    <Button
                      color="primary"
                      disabled={submitting}
                      endIcon={submitting ? <CircularProgress color='inherit' size={15} /> : null}
                      type={submitting ? 'button' : 'submit'}
                      variant="contained"
                    >
                      Save
                    </Button>
                  )
                  : (
                    <Button
                      color="primary"
                      disabled={submitting}
                      endIcon={submitting ? <CircularProgress color='inherit' size={15} /> : null}
                      onClick={() => submitting ? '' : setShowDisableDialog(true)}
                      variant="contained"
                    >
                      Save
                    </Button>
                  )
                }
                {showDisableDialog
                  && (
                    <DisableConfirmDialog 
                      data-testid="cct-disable-dialog"
                      handleClose={() => setShowDisableDialog(false)}
                      handleSubmit={(event: FormEvent) => {
                        setShowDisableDialog(false)
                        handleSubmit(event)
                      }}
                      open={showDisableDialog} 
                    />
                  )
                }
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Stack>
    </Box>
  )
}

export default ClientSettingsPage
